import { Ok, Err, type Result } from 'pratica'
const backendUrl = import.meta.env.VITE_BACKEND_BASE_URL

interface AuthResponse {
  access_token: string
  expires_on: number
  refresh_token: string
  refresh_token_expires_in: number
}

class AuthenticationService {
  private baseURL: string

  constructor() {
    this.baseURL = backendUrl
  }

  public async authenticate(code: string): Promise<Result<string, string>> {
    const response = await this.request<AuthResponse>('v1/auth', 'POST', { code })

    if (response.isOk()) {
      const authResponse = response.value() as AuthResponse
      this.storeTokens(authResponse)
      return Ok('success')
    } else {
      const errorMessage = response.value() as string
      return Err(errorMessage)
    }
  }

  public async refresh(): Promise<Result<string, string>> {
    const refresh_token = this.getRefreshToken();
    if (!refresh_token) {
      return Err('Refresh token not found');
    }
    const response = await this.request<AuthResponse>('v1/auth/refresh', 'POST', { refresh_token })

    if (response.isOk()) {
      const authResponse = response.value() as AuthResponse
      this.storeTokens(authResponse)
      return Ok('success')
    } else {
      const errorMessage = response.value() as string
      return Err(errorMessage)
    }
  }

  public logout(): void {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('access_token_expires_on')
    localStorage.removeItem('refresh_token_expires_on')
  }

  private storeTokens(authResponse: AuthResponse): void {
    localStorage.setItem('access_token', authResponse.access_token)
    localStorage.setItem('access_token_expires_on', `${authResponse.expires_on * 1000}`)
    localStorage.setItem('refresh_token', authResponse.refresh_token)
    localStorage.setItem('refresh_token_expires_on', `${Date.now() + authResponse.refresh_token_expires_in * 1000}`)
  }

  public getAccessToken(): string | null {
    const at = localStorage.getItem('access_token')
    return at
  }

  public getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  public isAuthenticated(): boolean {
    const at = this.getAccessToken()
    return at !== null
  }

  private async request<T>(url: string, method: string, body?: object): Promise<Result<T, string>> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    }
    const config: RequestInit = {
      method,
      headers
    }

    if (body) {
      config.body = JSON.stringify(body)
    }
    const authUrl = `${this.baseURL}${url}`
    const fetchResponse = await fetch(authUrl, config)

    if (!fetchResponse.ok) {
      const errorData = await fetchResponse.json()
      return Err(
        `Error: ${fetchResponse.status} - ${errorData?.message || fetchResponse.statusText}`
      )
    }
    const data = await fetchResponse.json()
    return Ok(data)
  }

  private handleError(error: unknown): Result<string, String> {
    if (error instanceof Error) {
      return Err(error.message)
    }

    if (error instanceof Response) {
      return Err(`HTTP error: ${error.statusText}`) //`HTTP error: ${error.statusText}`
    }

    return Err('An unknown error occurred')
  }
}

export const authService = new AuthenticationService()
